// /app/frontend/src/components/frugal-v2/components/InformativeEmailModal.jsx
// Modal for viewing informative (non-transaction) emails (< 100 lines)

import React, { useState } from 'react';
import { X, Mail, Calendar, Eye, EyeOff, Check } from 'lucide-react';
import RawEmailView from './RawEmailView';

const InformativeEmailModal = ({ email, onClose, onMarkAsRead, isProcessing, t }) => {
  const [showRawEmail, setShowRawEmail] = useState(false);

  if (!email) return null;

  const parsed = email.parsed_data || {};
  const rawEmail = email.raw_email || {};
  
  // Try multiple sources for email body (new system and old system)
  // Priority: body_text > raw_email.body_text > raw_email.body > parsed.body
  const emailBody = email.body_text || rawEmail.body_text || rawEmail.body || parsed.body || '';
  const emailHtml = email.body_html || rawEmail.html || rawEmail.body_html || '';
  const emailSubject = email.subject || rawEmail.subject || email.description || '';
  const transactionDate = parsed.date || email.created_at?.split('T')[0];
  
  // If no text body but has HTML, show HTML content
  const hasHtmlOnly = !emailBody && emailHtml;

  return (
    <>
      <div className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="bg-slate-900 rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden border border-blue-500/30">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-blue-500/20 bg-blue-500/10">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              <div>
                <h2 className="text-lg font-semibold text-white">{t('frugal.emails.informative')}</h2>
                <p className="text-xs text-blue-400">{email.email_from || 'Banco'}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
            {/* Subject */}
            <div className="bg-white/5 rounded-xl p-4">
              <label className="text-xs text-slate-500 block mb-1">{t('frugal.emails.subject')}</label>
              <div className="text-white font-medium">{emailSubject}</div>
            </div>

            {/* Email Body */}
            <div className="bg-white/5 rounded-xl p-4">
              <label className="text-xs text-slate-500 block mb-2">{t('frugal.emails.content')}</label>
              {/* Prefer text content for better readability, HTML only if no text */}
              {emailBody ? (
                <div className="text-slate-300 text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {emailBody}
                </div>
              ) : hasHtmlOnly ? (
                <div 
                  className="text-slate-300 text-sm max-h-64 overflow-y-auto bg-white rounded p-2"
                  dangerouslySetInnerHTML={{ __html: emailHtml }}
                />
              ) : (
                <div className="text-slate-500 text-sm italic">
                  {t('frugal.emails.noBody')}
                </div>
              )}
            </div>

            {/* Date */}
            {transactionDate && (
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="text-xs text-slate-500">{t('frugal.emails.date')}</div>
                  <div className="text-white">{transactionDate}</div>
                </div>
              </div>
            )}

            {/* Raw Email Toggle */}
            <button onClick={() => setShowRawEmail(!showRawEmail)}
              className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg">
              <div className="flex items-center gap-2">
                {showRawEmail ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                <span className="text-slate-300">{t('frugal.emails.viewRaw')}</span>
              </div>
            </button>
          </div>

          {/* Actions */}
          {email.status === 'pending_review' && (
            <div className="p-4 border-t border-white/10 space-y-2">
              <button 
                onClick={onMarkAsRead}
                disabled={isProcessing}
                className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                {t('frugal.emails.markAsRead')}
              </button>
              <button 
                onClick={onClose}
                className="w-full py-2 bg-white/5 hover:bg-white/10 text-slate-400 rounded-xl text-sm transition-colors"
              >
                {t('frugal.emails.close')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Raw Email Fullscreen */}
      <RawEmailView email={email} isOpen={showRawEmail} onClose={() => setShowRawEmail(false)} t={t} />
    </>
  );
};

export default InformativeEmailModal;
